// src/pages/EditarPremio.js

import React, { useEffect, useMemo, useState } from "react";
import axios, { BASE_URL } from "../axiosConfig";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Box,
  CheckCircle2,
  Gift,
  ImagePlus,
  Package,
  Save,
  Truck,
  Zap,
} from "lucide-react";
import "./EditarPremio.css";

const getImagemUrl = (img) => {
  if (!img) return "";
  return img.startsWith("http") ? img : `${BASE_URL}${img.replace(/\\/g, "/")}`;
};

function EditarPremio() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [valorPonto, setValorPonto] = useState(0.03);
  const [pontosSugeridos, setPontosSugeridos] = useState(0);
  const [valorPontosEditado, setValorPontosEditado] = useState(false);
  const [categorias, setCategorias] = useState([]);

  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    categoria: "Todos",
    preco_unitario: "",
    estoque: 0,
    valor_em_pontos: "",
    valor_em_pontos_promocional: "",
    promocao_inicio: "",
    promocao_fim: "",
    peso_kg: "",
    comprimento_cm: "",
    largura_cm: "",
    altura_cm: "",
    frete_gratis: false,
    exige_voltagem: false,
    voltagens_disponiveis: [],
  });

  const [imagemAtual, setImagemAtual] = useState("");
  const [novaImagem, setNovaImagem] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    const carregarPremio = async () => {
      try {
        const [premioRes, configRes, categoriasRes] = await Promise.all([
          axios.get(`/premios/${id}`),
          axios.get("/configuracoes/valor-ponto"),
          axios.get("/categorias-premios"),
        ]);

        setCategorias((categoriasRes.data || []).filter((c) => c.ativa));

        const {
          nome,
          descricao,
          categoria,
          preco_unitario,
          estoque,
          imagem,
          valor_em_pontos,
          valor_em_pontos_promocional,
          promocao_inicio,
          promocao_fim,
          peso_kg,
          comprimento_cm,
          largura_cm,
          altura_cm,
          frete_gratis,
          exige_voltagem,
          voltagens_disponiveis,
        } = premioRes.data;

        const valorPontoAtual = configRes.data.valor || 0.03;

        setFormData({
          nome,
          descricao,
          categoria: categoria || "Todos",
          preco_unitario,
          estoque,
          valor_em_pontos: valor_em_pontos?.toString() || "",
          valor_em_pontos_promocional: valor_em_pontos_promocional || "",
          promocao_inicio: promocao_inicio?.split("T")[0] || "",
          promocao_fim: promocao_fim?.split("T")[0] || "",
          peso_kg: peso_kg || "",
          comprimento_cm: comprimento_cm || "",
          largura_cm: largura_cm || "",
          altura_cm: altura_cm || "",
          frete_gratis: Number(frete_gratis || 0) === 1,
          exige_voltagem: Number(exige_voltagem || 0) === 1,
          voltagens_disponiveis: String(voltagens_disponiveis || "")
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean),
        });

        setImagemAtual(imagem);
        setValorPonto(valorPontoAtual);
        calcularPontos(preco_unitario, valorPontoAtual);
      } catch (err) {
        console.error("Erro ao buscar prêmio:", err);
        setErro(err.response?.data?.error || "Erro ao carregar dados do prêmio.");
      }
    };

    carregarPremio();
  }, [id]);

  const avisoFreteIncompleto = useMemo(
    () =>
      ["peso_kg", "comprimento_cm", "largura_cm", "altura_cm"].some(
        (campo) => !formData[campo]
      ),
    [formData]
  );

  const calcularPontos = (preco, valorPontoAtual) => {
    const pontos = Math.ceil(
      (parseFloat(preco || 0) * 1.2) / parseFloat(valorPontoAtual || 0.03)
    );
    setPontosSugeridos(pontos);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "preco_unitario") calcularPontos(value, valorPonto);
    if (name === "valor_em_pontos") setValorPontosEditado(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      setErro("Formato inválido. Use JPG, PNG ou GIF.");
      return;
    }

    if (file.size > maxSize) {
      setErro("Imagem muito grande. O limite é 5MB.");
      return;
    }

    setNovaImagem(file);
    setErro("");
  };

  const handleVoltagemChange = (voltagem, checked) => {
    setFormData((prev) => {
      const atuais = prev.voltagens_disponiveis || [];

      return {
        ...prev,
        voltagens_disponiveis: checked
          ? [...atuais, voltagem]
          : atuais.filter((item) => item !== voltagem),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErro("");
    setMensagem("");
    setIsSubmitting(true);
    setUploadProgress(0);

    const formDataToSend = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "imagem") return;

      if (key === "frete_gratis" || key === "exige_voltagem") {
        formDataToSend.append(key, value ? "true" : "false");
        return;
      }

      if (key === "voltagens_disponiveis") {
        formDataToSend.append(key, Array.isArray(value) ? value.join(",") : value || "");
        return;
      }

      formDataToSend.append(key, value ?? "");
    });

    if (novaImagem) {
      formDataToSend.append("imagem", novaImagem);
    }

    try {
      const response = await axios.post(`/premios/${id}/update`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.success) {
        setMensagem(response.data.message || "Prêmio atualizado com sucesso!");

        if (response.data.data?.imagem) {
          setImagemAtual(response.data.data.imagem);
        }

        setTimeout(() => navigate("/premios"), 2000);
      } else {
        setErro(response.data.error || "Erro ao atualizar prêmio.");
      }
    } catch (err) {
      console.error("Erro na requisição:", err);
      setErro(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Erro ao conectar com o servidor."
      );
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="editarPremioAdmin-page">
      <div className="editarPremioAdmin-topbar">
        <div>
          <span className="editarPremioAdmin-kicker">Catálogo de recompensas</span>

          <h2>Editar Prêmio</h2>

          <p>
            Atualize dados do prêmio, imagem, estoque, pontuação, promoção,
            voltagem e informações de frete.
          </p>
        </div>

        <Link to="/premios" className="editarPremioAdmin-btn secondary">
          <ArrowLeft size={16} />
          Voltar para prêmios
        </Link>
      </div>

      {mensagem && (
        <div className="editarPremioAdmin-alert success">
          <CheckCircle2 size={16} />
          {mensagem}
        </div>
      )}

      {erro && <div className="editarPremioAdmin-alert danger">{erro}</div>}

      <form className="editarPremioAdmin-form" onSubmit={handleSubmit}>
        <div className="editarPremioAdmin-layout">
          <section className="editarPremioAdmin-card">
            <div className="editarPremioAdmin-cardHeader">
              <div>
                <h3>Dados principais</h3>
                <span>Informações básicas exibidas no catálogo.</span>
              </div>

              <Gift size={18} />
            </div>

            <div className="editarPremioAdmin-grid">
              <div className="editarPremioAdmin-field">
                <label>Nome do prêmio</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-field">
                <label>Categoria</label>
                <select
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.nome}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="editarPremioAdmin-field editarPremioAdmin-fieldFull">
                <label>Descrição</label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-field">
                <label>Estoque</label>
                <input
                  type="number"
                  name="estoque"
                  value={formData.estoque}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />

                {Number(formData.estoque || 0) <= 5 && (
                  <div className="editarPremioAdmin-warning compact">
                    Estoque baixo.
                  </div>
                )}
              </div>

              <div className="editarPremioAdmin-field">
                <label>Trocar imagem</label>
                <input
                  type="file"
                  name="imagem"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                />
                <small>Apenas JPG, PNG ou GIF até 5MB.</small>
              </div>
            </div>
          </section>

          <section className="editarPremioAdmin-card">
            <div className="editarPremioAdmin-cardHeader">
              <div>
                <h3>Pontos e promoção</h3>
                <span>Atualize o custo em pontos e período promocional.</span>
              </div>

              <Zap size={18} />
            </div>

            <div className="editarPremioAdmin-grid">
              <div className="editarPremioAdmin-field">
                <label>Preço pago</label>
                <input
                  type="number"
                  step="0.01"
                  name="preco_unitario"
                  value={formData.preco_unitario}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-pontosSugeridos">
                <span>Pontos sugeridos</span>
                <strong>{pontosSugeridos.toLocaleString("pt-BR")} pts</strong>
              </div>

              <div className="editarPremioAdmin-field">
                <label>Pontos necessários</label>
                <input
                  type="number"
                  name="valor_em_pontos"
                  value={formData.valor_em_pontos}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="editarPremioAdmin-field">
                <label>Pontos promocionais</label>
                <input
                  type="number"
                  name="valor_em_pontos_promocional"
                  value={formData.valor_em_pontos_promocional}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-field">
                <label>Início da promoção</label>
                <input
                  type="date"
                  name="promocao_inicio"
                  value={formData.promocao_inicio}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-field">
                <label>Fim da promoção</label>
                <input
                  type="date"
                  name="promocao_fim"
                  value={formData.promocao_fim}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              {valorPontosEditado &&
                String(formData.valor_em_pontos) !== String(pontosSugeridos) && (
                  <div className="editarPremioAdmin-warning editarPremioAdmin-fieldFull">
                    Você está alterando os pontos sugeridos. Sugerido:{" "}
                    <strong>{pontosSugeridos.toLocaleString("pt-BR")} pts</strong>{" "}
                    / Atual:{" "}
                    <strong>
                      {Number(formData.valor_em_pontos || 0).toLocaleString("pt-BR")} pts
                    </strong>
                  </div>
                )}
            </div>
          </section>

          <section className="editarPremioAdmin-card">
            <div className="editarPremioAdmin-cardHeader">
              <div>
                <h3>Voltagem</h3>
                <span>Configure se o cliente deverá escolher voltagem.</span>
              </div>

              <Box size={18} />
            </div>

            <div className="editarPremioAdmin-cardBody">
              <label className="editarPremioAdmin-toggleBox">
                <input
                  type="checkbox"
                  checked={formData.exige_voltagem}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      exige_voltagem: e.target.checked,
                      voltagens_disponiveis: e.target.checked
                        ? prev.voltagens_disponiveis
                        : [],
                    }))
                  }
                  disabled={isSubmitting}
                />

                <span className="editarPremioAdmin-toggleTrack">
                  <span className="editarPremioAdmin-toggleThumb" />
                </span>

                <span>
                  <strong>Este prêmio exige escolha de voltagem</strong>
                  <small>Use para eletrodomésticos, eletrônicos e similares.</small>
                </span>
              </label>

              {formData.exige_voltagem && (
                <div className="editarPremioAdmin-checkGrid">
                  {["110V", "220V", "Bivolt"].map((voltagem) => (
                    <label key={voltagem} className="editarPremioAdmin-checkItem">
                      <input
                        type="checkbox"
                        checked={formData.voltagens_disponiveis.includes(voltagem)}
                        onChange={(e) =>
                          handleVoltagemChange(voltagem, e.target.checked)
                        }
                        disabled={isSubmitting}
                      />
                      <span>{voltagem}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="editarPremioAdmin-card">
            <div className="editarPremioAdmin-cardHeader">
              <div>
                <h3>Frete e dimensões</h3>
                <span>Dados opcionais para cálculo automático de frete.</span>
              </div>

              <Truck size={18} />
            </div>

            <div className="editarPremioAdmin-grid">
              <div className="editarPremioAdmin-field">
                <label>Peso</label>
                <input
                  type="number"
                  step="0.01"
                  name="peso_kg"
                  placeholder="kg"
                  value={formData.peso_kg}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-field">
                <label>Comprimento</label>
                <input
                  type="number"
                  name="comprimento_cm"
                  placeholder="cm"
                  value={formData.comprimento_cm}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-field">
                <label>Largura</label>
                <input
                  type="number"
                  name="largura_cm"
                  placeholder="cm"
                  value={formData.largura_cm}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-field">
                <label>Altura</label>
                <input
                  type="number"
                  name="altura_cm"
                  placeholder="cm"
                  value={formData.altura_cm}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarPremioAdmin-fieldFull">
                <label className="editarPremioAdmin-toggleBox">
                  <input
                    type="checkbox"
                    checked={formData.frete_gratis}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        frete_gratis: e.target.checked,
                      }))
                    }
                    disabled={isSubmitting}
                  />

                  <span className="editarPremioAdmin-toggleTrack">
                    <span className="editarPremioAdmin-toggleThumb" />
                  </span>

                  <span>
                    <strong>Frete grátis para este prêmio</strong>
                    <small>Marcado quando o cliente não deve pagar frete.</small>
                  </span>
                </label>
              </div>

              {avisoFreteIncompleto && (
                <div className="editarPremioAdmin-warning editarPremioAdmin-fieldFull">
                  Sem preencher peso e dimensões, o frete não será calculado
                  automaticamente para este prêmio.
                </div>
              )}
            </div>
          </section>

          <aside className="editarPremioAdmin-card editarPremioAdmin-previewCard">
            <div className="editarPremioAdmin-cardHeader">
              <div>
                <h3>Prévia da imagem</h3>
                <span>Veja a imagem atual ou a nova imagem selecionada.</span>
              </div>

              <ImagePlus size={18} />
            </div>

            <div className="editarPremioAdmin-previewBox">
              {novaImagem ? (
                <img src={URL.createObjectURL(novaImagem)} alt="Prévia da nova imagem" />
              ) : imagemAtual ? (
                <img src={getImagemUrl(imagemAtual)} alt="Imagem atual" />
              ) : (
                <div className="editarPremioAdmin-previewEmpty">
                  <Package size={28} />
                  <span>Nenhuma imagem disponível</span>
                </div>
              )}
            </div>

            <div className="editarPremioAdmin-previewInfo">
              <strong>{formData.nome || "Nome do prêmio"}</strong>
              <span>
                {formData.valor_em_pontos
                  ? `${Number(formData.valor_em_pontos).toLocaleString("pt-BR")} pts`
                  : "Pontuação ainda não definida"}
              </span>

              <small>{novaImagem ? "Nova imagem selecionada" : "Imagem atual"}</small>
            </div>
          </aside>
        </div>

        <div className="editarPremioAdmin-submitBar">
          <button
            type="submit"
            className="editarPremioAdmin-btn primary"
            disabled={isSubmitting}
          >
            <Save size={16} />
            {isSubmitting ? `Salvando... ${uploadProgress}%` : "Salvar alterações"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditarPremio;